import React, { FC, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ItemDetails from '../ItemDetails';
import AssignDriverModal from '../AssignDriver';
import { GetFilteredLoadManagements_getFilteredLoadManagements as Load } from '../../../types/schemaTypes';
import './LoadItem.scss';
import SplitLoadModal from '../../SplitLoadModal/SplitLoadModal';
import { LoadStatusEnum } from '../../../types/LoadStatusEnum';
import { DriverLoadStatusEnum } from '../../../types/DriverLoadStatusEnum';
import { Routes } from '../../../utils';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import { useStoreContext } from '../../App/StoreProvider';
import { formatDateUtcToLocal } from '../../../utils/helpers/datetimeHelper';
interface LoadItemProps {
    abortRequest: () => void;
    jobId: number | null | undefined;
    load: Load;
    isChecked: boolean;
    onChange: (checkedStatus: boolean) => void;
    refetch: () => void;
}

interface DetailOption {
    displayAssignDriver: boolean;
    statusLabel: string;
    statusDetails: string;
    displayMore: boolean;
    loadStatusLabel: string;
}

const getLoadDetails = (
    status: string,
    driverLoadStatus: string,
    secondaryDriverLoadStatus: string,
    secondaryDriverId: number,
    isSplit: boolean,
    rejectReason: string,
    rejectedDateTime: string,
    userName: string,
    rejectedDriverName: string,
): DetailOption => {
    const details: DetailOption = {
        displayAssignDriver: false,
        statusLabel: '',
        statusDetails: '',
        displayMore: false,
        loadStatusLabel: '',
    };
    if (status === LoadStatusEnum.CREATED) {
        if (rejectedDateTime != '') {
            if (rejectReason === 'Timeout') {
                details.statusLabel = DriverLoadStatusEnum.REJECTED;
                details.statusDetails = 'Load ' + rejectReason + ' on ' + formatDateUtcToLocal(rejectedDateTime);
                details.displayAssignDriver = true;
            } else {
                details.statusLabel = DriverLoadStatusEnum.REJECTED;
                details.statusDetails =
                    'Rejected by ' +
                    (rejectReason === 'Rejected by Dispatch'
                        ? 'dispatch ' + userName
                        : 'driver ' + rejectedDriverName) +
                    ' on ' +
                    formatDateUtcToLocal(rejectedDateTime);
                details.displayAssignDriver = true;
            }
        }
        details.displayAssignDriver = true;
    } else if (status === LoadStatusEnum.ASSIGNED) {
        if (driverLoadStatus === DriverLoadStatusEnum.ACCEPTED) {
            details.displayMore = true;
        } else {
            details.statusLabel = DriverLoadStatusEnum.PENDING;
        }
    } else if (status === LoadStatusEnum.LOADED) {
        if (secondaryDriverId === 0 && isSplit && rejectedDateTime != '') {
            if (rejectReason === 'Timeout') {
                details.statusLabel = DriverLoadStatusEnum.REJECTED;
                details.statusDetails = 'Load ' + rejectReason + ' on ' + formatDateUtcToLocal(rejectedDateTime);
            } else {
                details.loadStatusLabel = LoadStatusEnum.LOADED;
                details.statusDetails =
                    'Rejected by ' +
                    (rejectReason === 'Rejected by Dispatch'
                        ? 'dispatch ' + userName
                        : 'driver ' + rejectedDriverName) +
                    ' on ' +
                    formatDateUtcToLocal(rejectedDateTime);
                details.displayAssignDriver = true;
                details.statusLabel = DriverLoadStatusEnum.REJECTED;
            }
            details.displayMore = true;
            details.displayAssignDriver = true;
        } else if (secondaryDriverLoadStatus === DriverLoadStatusEnum.PENDING) {
            details.loadStatusLabel = LoadStatusEnum.LOADED;
            details.displayMore = true;
            details.statusLabel = DriverLoadStatusEnum.PENDING;
        } else {
            details.loadStatusLabel = LoadStatusEnum.LOADED;
            details.displayMore = true;
        }
    } else if (status === LoadStatusEnum.ON_SITE) {
        if (secondaryDriverId === 0 && isSplit && rejectedDateTime != '') {
            if (rejectReason === 'Timeout') {
                details.statusLabel = DriverLoadStatusEnum.REJECTED;
                details.statusDetails = 'Load ' + rejectReason + ' on ' + formatDateUtcToLocal(rejectedDateTime);
            } else {
                details.loadStatusLabel = LoadStatusEnum.LOADED;
                details.statusDetails =
                    'Rejected by ' +
                    (rejectReason === 'Rejected by Dispatch'
                        ? 'dispatch ' + userName
                        : 'driver ' + rejectedDriverName) +
                    ' on ' +
                    formatDateUtcToLocal(rejectedDateTime);
                details.displayAssignDriver = true;
                details.statusLabel = DriverLoadStatusEnum.REJECTED;
            }
            details.displayMore = true;
            details.displayAssignDriver = true;
        } else if (secondaryDriverLoadStatus === DriverLoadStatusEnum.PENDING) {
            details.loadStatusLabel = LoadStatusEnum.ON_SITE;
            details.displayMore = true;
            details.statusLabel = DriverLoadStatusEnum.PENDING;
        } else {
            details.loadStatusLabel = LoadStatusEnum.ON_SITE;
            details.displayMore = true;
        }
    } else {
        details.displayMore = true;
    }

    return details;
};

const LoadItem: FC<LoadItemProps> = ({
    abortRequest,
    jobId,
    load,
    isChecked,
    onChange,
    refetch,
}: LoadItemProps): React.ReactElement => {
    const canAddDriver = useAuthContext().userHasPermission(PermissionEnum.ManageLoads);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [state] = useStoreContext();

    const {
        loadList: { filters, searchPhrase },
    } = state;
    const {
        loadId,
        lofaId,
        boxNumber,
        carrierName,
        driverLoadStatus,
        driverName,
        proppantName,
        loadingFacility,
        sandPO,
        status,
        weight,
        atLofaIn,
        atLofaOut,
        atDestinationIn,
        atDestinationOut,
        truckNumber,
        chassisNumber,
        sandTicket,
        transportationPo,
        regionId,
        secondaryDriverId,
        secondaryDriverLoadStatus,
        rejectReason,
        assignedDateTime,
        rejectedDateTime,
        userName,
        rejectedDriverName,
    } = load;
    const { displayAssignDriver, statusLabel, statusDetails, displayMore, loadStatusLabel } = getLoadDetails(
        status,
        driverLoadStatus || '',
        secondaryDriverLoadStatus || '',
        secondaryDriverId || 0,
        load.isSplit || false,
        rejectReason || '',
        rejectedDateTime || '',
        userName || '',
        rejectedDriverName || '',
    );
    const canSplitLoad = useAuthContext().userHasPermission(PermissionEnum.SplitLoads);
    const handleOnClickHide = (): void => {
        setShowModal(false);
    };
    const UNAVAILABLE_DATE = 'Not available';

    const openLoadProfile = (loadId: number, boxNumber: string): void => {
        const URL = `${window.location.origin}${Routes.LOAD_PROFILE.route}`;
        const newTab = window.open(URL + loadId, '_blank');
        setTimeout(function(): void {
            if (newTab) {
                newTab.document.title = loadId.toString();
            }
        }, 100);
    };

    const checkForStatus = (status: string): boolean => {
        if (status === LoadStatusEnum.CREATED || status === LoadStatusEnum.CREATED_NOT_SAVED) {
            return true;
        }
        return false;
    };

    return (
        <div>
            <div className={'LoadItem-Container ' + (isChecked ? 'LoadItem-Selected' : '')}>
                <Col md="auto" className='checkbox-column'>
                    <div className="mb-3">
                        {checkForStatus(status) ? (
                            <input
                                type="checkbox"
                                className="LoadItem-Checkbox"
                                checked={isChecked}
                                onChange={(event): void => {
                                    // abortRequest();
                                    onChange(event.currentTarget.checked);
                                }}
                            />
                        ) : (
                            <input
                                type="checkbox"
                                className="LoadItem-Checkbox"
                                checked={isChecked}
                                title="Cannot delete load"
                                disabled={true}
                                onChange={(event): void => {
                                    // abortRequest();
                                    onChange(event.currentTarget.checked);
                                }}
                            />
                        )}
                    </div>
                </Col>
                <Col md={3} className="d-flex flex-column justify-content-between my-flex-container custom-width">
                    <Row>
                        <span className="LoadItem-Name" onClick={(): void => openLoadProfile(loadId, boxNumber)}>
                            {`Load #: ${loadId}`}
                        </span>
                    </Row>
                    <ItemDetails label="Carrier:" info={carrierName || ''} />
                    <ItemDetails label="Driver:" info={driverName} />
                </Col>
                <Col md={4} className="d-flex flex-column justify-content-between my-flex-container custom-width">
                    <ItemDetails label="Proppant:" info={proppantName} />
                    <ItemDetails label="Loading Facility:" info={loadingFacility} />
                    <ItemDetails label="Sand PO:" info={sandPO} />
                </Col>
                <Col md={2} className="d-flex flex-column justify-content-between my-flex-container custom-width">
                    <ItemDetails label="Status:" info={status} />
                    {(!boxNumber || !boxNumber.startsWith('8888')) && (
                        <ItemDetails label="Box #:" info={boxNumber || 'N/A'} />
                    )}
                    <ItemDetails
                        label="Weight:"
                        info={weight ? weight.toLocaleString() + ' (' + (weight / 2000).toFixed(2) + 'T)' : weight}
                    />
                </Col>
                <Col md="auto" className="d-flex flex-column justify-content-between my-flex-container customColumn custom-width">
                    {displayMore ? (
                        <div className='LoadItem-more-Container'>
                            <div className="LoadItem-LinkContainer">
                                <div className="LoadItem-StatusDetails-More">
                                    {statusLabel === DriverLoadStatusEnum.PENDING && (
                                        <Row className="SplitLoadItem-Pending">
                                            <i className="material-icons LoadItem-PendingIcon">schedule</i>
                                            <p className="LoadItem-StatusText">{statusLabel}</p>
                                        </Row>
                                    )}
                                    {statusLabel === DriverLoadStatusEnum.REJECTED && (
                                        <Row className="LoadItem-Rejected-More">
                                            <i className="material-icons LoadItem-RejectedIcon">cancel</i>
                                            <p className="LoadItem-StatusText-Rejected">{statusDetails}</p>
                                        </Row>
                                    )}
                                    {statusLabel === DriverLoadStatusEnum.CANCELED && (
                                        <Row className="LoadItem-Rejected-Details">
                                            <i className="material-icons LoadItem-RejectedIcon">highlight_off</i>
                                            <p className="LoadItem-StatusText">{statusLabel}</p>
                                        </Row>
                                    )}
                                </div>
                            </div>
                            <br />
                            <br />
                            <Row className={`${!canAddDriver && 'LoadItem-AddDriver-Disable'} custom-driver-button`}>
                                {displayAssignDriver && (
                                    <AssignDriverModal
                                        jobId={jobId}
                                        proppantName={proppantName || ''}
                                        lofa={loadingFacility || ''}
                                        lofaId={lofaId || 0}
                                        regionId={regionId || 0}
                                        loadId={loadId}
                                        isFromLoads={true}
                                        isSplitLoad={load.isSplit || false}
                                        sandPO={load.sandPO || ''}
                                        driverName={driverName || ''}
                                        refetch={refetch}
                                    />
                                )}
                            </Row>
                            <Row onClick={(): void => setShowModal(true)}>
                                {(loadStatusLabel === LoadStatusEnum.LOADED ||
                                    loadStatusLabel === LoadStatusEnum.ON_SITE) &&
                                    canSplitLoad &&
                                    !load.isSplit && (
                                        <Row className="Split-Load-IsSplit">
                                            <i className="material-icons Split-Load-IsSplit-Icon">call_split</i>
                                            <p>Split Load</p>
                                        </Row>
                                    )}
                            </Row>
                            <Row>
                                {load.isSplit && (
                                    <Row
                                        className={` Split-Load-Info-Green ${
                                            statusLabel !== DriverLoadStatusEnum.PENDING ? 'Split-Load-Info-Green2' : ''
                                        }`}
                                    >
                                        <i className="material-icons Split-Load-IsSplit-Icon">call_split</i>
                                        <p>Split Load</p>
                                    </Row>
                                )}
                            </Row>
                            <SplitLoadModal
                                onClickShow={() => setShowModal(true)}
                                show={showModal}
                                onClickHide={handleOnClickHide}
                                loadId={loadId}
                                lofaId={lofaId}
                                jobId={jobId}
                                proppantName={proppantName || ''}
                                lofa={loadingFacility || ''}
                                sandPO={sandPO || ''}
                                regionId={regionId || 0}
                                loadListfilters={filters}
                                loadListsearchPhrase={searchPhrase}
                                driverName={driverName || ''}
                                refetch={refetch}
                            />
                            <Row
                                className={` LoadItem-LinkContainer ${
                                    statusLabel === DriverLoadStatusEnum.REJECTED ||
                                    ((secondaryDriverId === null || secondaryDriverId === 0) &&
                                        (status === LoadStatusEnum.LOADED || status === LoadStatusEnum.ON_SITE)) ||
                                    secondaryDriverLoadStatus === DriverLoadStatusEnum.PENDING ||
                                    (load.isSplit && load.status === LoadStatusEnum.DELIVERED)
                                        ? load.isSplit && secondaryDriverId === 0
                                            ? ''
                                            : 'LoadItem-LinkContainer-More3'
                                        : 'LoadItem-LinkContainer-More2'
                                }`}
                                onClick={(): void => setIsDetailsOpen(!isDetailsOpen)}
                            >
                                <p className="LoadItem-LinkText">{isDetailsOpen ? 'Less' : 'More'}</p>
                                <i className="material-icons LoadItem-Icon">
                                    {isDetailsOpen ? 'expand_less' : 'expand_more'}
                                </i>
                            </Row>
                        </div>
                    ) : (
                        <div
                            className={`${
                                status === LoadStatusEnum.CREATED
                                    ? canAddDriver
                                        ? 'LoadItem-LinkContainer'
                                        : 'LoadItem-AddDriver-Disable'
                                    : ''
                            }`}
                            id={statusLabel ? 'LoadItem-AddDriver-Enable' : ''}
                        >
                            <div className="LoadItem-StatusDetails">
                                {statusLabel === DriverLoadStatusEnum.PENDING && (
                                    <Row className="LoadItem-Pending">
                                        <i className="material-icons LoadItem-PendingIcon">schedule</i>
                                        <p className="LoadItem-StatusText">{statusLabel}</p>
                                    </Row>
                                )}
                                {statusLabel === DriverLoadStatusEnum.REJECTED && (
                                    <Row className="LoadItem-Rejected">
                                        <i className="material-icons LoadItem-RejectedIcon">cancel</i>
                                        <p className="LoadItem-StatusText-Rejected">{statusDetails}</p>
                                    </Row>
                                )}
                            </div>
                            <Row className={`${!canAddDriver && 'LoadItem-AddDriver-Disable'} custom-driver-button`}>
                                {displayAssignDriver && (
                                    <AssignDriverModal
                                        jobId={jobId}
                                        proppantName={proppantName || ''}
                                        lofa={loadingFacility || ''}
                                        lofaId={lofaId || 0}
                                        regionId={regionId || 0}
                                        loadId={loadId}
                                        isFromLoads={true}
                                        isSplitLoad={load.isSplit || false}
                                        sandPO={load.sandPO || ''}
                                        driverName={driverName || ''}
                                        refetch={refetch}
                                    />
                                )}
                            </Row>
                        </div>  
                    )}
                </Col>
            </div>
            {isDetailsOpen && (
                <div className="LoadItem-DetailsContainer">
                    <div className="LoadItem-DetailsSection">
                        <ItemDetails label="Chassis #:" info={chassisNumber} />
                        <ItemDetails label="Truck #:" info={truckNumber} />
                    </div>
                    <div className="LoadItem-Divider" />
                    <div className="LoadItem-DetailsSection">
                        <ItemDetails
                            label="LoFa In:"
                            info={atLofaIn ? formatDateUtcToLocal(atLofaIn) : UNAVAILABLE_DATE}
                        />
                        <ItemDetails
                            label="LoFa Out:"
                            info={atLofaOut ? formatDateUtcToLocal(atLofaOut) : UNAVAILABLE_DATE}
                        />
                    </div>
                    <div className="LoadItem-Divider" />
                    <div className="LoadItem-DetailsSection">
                        <ItemDetails
                            label="Dest In:"
                            info={atDestinationIn ? formatDateUtcToLocal(atDestinationIn) : UNAVAILABLE_DATE}
                        />
                        <ItemDetails
                            label="Dest Out:"
                            info={atDestinationOut ? formatDateUtcToLocal(atDestinationOut) : UNAVAILABLE_DATE}
                        />
                    </div>
                    <div className="LoadItem-Divider" />
                    <div className="LoadItem-DetailsSection">
                        <ItemDetails label="Sand Ticket:" info={sandTicket || sandPO} />
                        <ItemDetails label="Trans. PO:" info={transportationPo} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoadItem;

